import useSWR from 'swr';

import { Partner, PartnerListResponse } from 'containers/Partners/types';
import { IDetailResponse, IPaginatedResponse } from 'utils/types';

import { fetcher } from './utils';

export interface IWalletTransaction {
  id: number;
  transaction_type: string;
  amount: string;
  reason?: string | null;
  source_type?: string | null;
  source_id?: string | null;
  created_at?: string | null;
}

export const usePartnerList = (params?: Record<string, any>) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<PartnerListResponse>(
    { url: '/v4/dash/partners', params },
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const usePartner = (id?: number | string | null) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<Partner>>(
    id ? `/v4/dash/partners/${id}` : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const fetchPartnerById = async (id: number | string) => {
  const response = (await fetcher(`/v4/dash/partners/${id}`)) as IDetailResponse<Partner>;

  return response.data;
};

const fetchPartnersByIds = async (ids: Array<number | string>) => {
  const responses = await Promise.all(
    ids.map(id => fetcher(`/v4/dash/partners/${id}`) as Promise<IDetailResponse<Partner>>)
  );

  return responses.map(response => response.data);
};

export const usePartnersByIds = (ids?: Array<number | string> | null) => {
  const shouldFetch = Boolean(ids?.length);
  const key = shouldFetch ? ['partnersByIds', ...(ids as Array<number | string>)] : null;

  const { data, isLoading, error, mutate, isValidating } = useSWR<Partner[]>(key, () =>
    fetchPartnersByIds(ids as Array<number | string>)
  );

  return { data, isLoading, error, mutate, isValidating };
};

export const useSubPartnerList = (partnerId: number | string, params?: Record<string, any>) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<PartnerListResponse>(
    partnerId ? { url: `/v4/dash/partners/${partnerId}/sub_partners`, params } : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useWalletTransactionList = (
  walletId: number | string,
  params?: Record<string, any>
) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<IWalletTransaction>
  >(walletId ? { url: `/v4/dash/wallets/${walletId}/transactions`, params } : null, fetcher);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};
